import { WifiOff } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { useToast } from './use-toast';

import { useOnlineStatus } from '@/shared/hooks/use-online-status';
import { cn } from '@/shared/lib/cn';

export interface OfflineBannerProps {
  /** Quando provido, sobrepoe a deteccao automatica via `navigator.onLine`. Util para testes/storybook. */
  forceOffline?: boolean;
  className?: string;
  testID?: string;
  /**
   * Quando `false`, suprime o toast "Conexao restabelecida" no retorno
   * online. Default `true` para conforto do usuario; testes que verificam
   * apenas o banner em isolamento podem passar `false` para nao depender
   * de ToastProvider no setup.
   */
  notifyOnReconnect?: boolean;
}

/**
 * Banner sticky no topo da viewport quando o navegador esta offline.
 *
 * Phase 15 polish:
 *  - Anima `slide-down` ao aparecer (250ms cubic-bezier).
 *  - Debounce do estado offline (1s) via `useOnlineStatus` — evita flicker
 *    em quedas espurias (ex: troca de adaptador de rede no Windows).
 *  - Toast `success` "Conexao restabelecida" ao voltar online (so dispara se
 *    realmente esteve offline antes — nao no primeiro render).
 *  - Mantem `role="status"` + `aria-live="polite"` para leitores de tela.
 *
 * Posicionamento: montado em `_layout.tsx` acima de `<Outlet/>` para ficar
 * sempre no topo absoluto. Usa `--z-sticky` (10) para ficar acima do shell
 * mas abaixo de modais (--z-modal=1000) e toasts (--z-toast=1200).
 *
 * Feita a partir da OPCAO A "Faixa amber sticky com slide-down" (ver
 * __mocks__/edge/offline-banner/option-a.tsx).
 */
export function OfflineBanner({
  forceOffline,
  className,
  testID,
  notifyOnReconnect = true,
}: OfflineBannerProps) {
  const onlineFromHook = useOnlineStatus();
  const offline = forceOffline ?? !onlineFromHook;
  const wasOfflineRef = useRef(false);
  const hasMountedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (offline) wasOfflineRef.current = true;
      return;
    }
    if (offline) {
      wasOfflineRef.current = true;
      return;
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      if (notifyOnReconnect) {
        toast('Conexão restabelecida.', 'success');
      }
    }
  }, [offline, notifyOnReconnect, toast]);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testID ?? 'offline-banner'}
      className={cn(
        'sticky top-0 z-[var(--z-sticky)] flex animate-slide-down items-center justify-center gap-2 bg-warning/15 px-4 py-2 text-label-md font-semibold text-warning backdrop-blur-md motion-reduce:animate-none',
        className,
      )}
    >
      <WifiOff size={16} aria-hidden />
      <span>Você está offline. Algumas ações podem falhar.</span>
    </div>
  );
}
